import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { isRTLLocale } from '@wix/cashier-common/dist/src/utils/isRTLLocale';
import cn from 'classnames';
import React from 'react';
import {
  applyColumnsToMFields,
  sortMFieldsByCountryFactory,
  UIMFieldMeta,
} from '../../utils/mandatoryFieldsMeta';
import { DeviceType } from '../../types/DeviceType';
import { MFieldName } from '../../types/MandatoryFields';
import { Theme } from '../../types/Theme';
import { DH } from '../../utils/DataHook';
import { isMobileWidth, getDocumentWidth } from '../../utils/documentWidth';
import { Field } from './Field';
import * as s from './styles.scss';
import { MFieldsState } from './types';
import { createMFieldState } from './utils';

interface PaymentDetailsViewProps {
  theme: Theme;
  deviceType: DeviceType;
  countryCode: string;
  locale: string;
  paymentMethod: PaymentMethod;
  isCollapsed: boolean;
  shouldApplySiteStyles: boolean;
  allMFieldsState: MFieldsState;
  uiMFieldsMeta: UIMFieldMeta[];
  host?: string;

  fieldRef(mFieldName: MFieldName, inputElement: HTMLInputElement): void;
  onChange(mFieldName: MFieldName, newValue: string, isValid: boolean): void;
}

export class PaymentDetailsView extends React.Component<PaymentDetailsViewProps> {
  private paymentDetailsRef: React.RefObject<HTMLDivElement>;
  static defaultProps: Partial<PaymentDetailsViewProps> = {
    theme: Theme.default,
    deviceType: 'desktop',
  };

  constructor(props) {
    super(props);
    this.paymentDetailsRef = React.createRef();
  }

  renderMandatoryFields = () => {
    const {
      deviceType,
      shouldApplySiteStyles,
      theme,
      allMFieldsState,
      countryCode,
      fieldRef,
      host,
      locale,
      paymentMethod,

      onChange,
    } = this.props;

    let uiMFieldsMeta = this.props.uiMFieldsMeta
      .slice()
      .sort((mf1, mf2) =>
        sortMFieldsByCountryFactory(countryCode)(mf1.name, mf2.name)
      );

    if (
      deviceType === 'desktop' &&
      !isMobileWidth(
        this.paymentDetailsRef?.current?.offsetWidth || getDocumentWidth()
      )
    ) {
      uiMFieldsMeta = applyColumnsToMFields(uiMFieldsMeta);
    }

    return uiMFieldsMeta.map((field) => {
      let className: string = s.field;

      if (field.column === 'left') {
        className = s.fieldLeft;
      } else if (field.column === 'right') {
        className = s.fieldRight;
      }

      return (
        <div
          key={field.name}
          data-hook={DH.PaymentDetails.FieldWrapper(field.name)}
          className={cn({
            [className]: true,
            [s.modalTheme]: theme === Theme.modal,
          })}
        >
          <Field
            deviceType={deviceType}
            data={field}
            host={host}
            locale={locale}
            theme={theme}
            formFieldData={
              allMFieldsState[field.name] || createMFieldState(field.name)
            }
            countryCode={countryCode || allMFieldsState.countryCode?.value}
            values={field.values}
            shouldApplySiteStyles={shouldApplySiteStyles}
            paymentMethod={paymentMethod}
            inputRef={(inputElement) => fieldRef(field.name, inputElement)}
            onChange={({ value, isValid }) => {
              onChange(field.name, value, isValid);
            }}
          />
        </div>
      );
    });
  };

  render() {
    const { deviceType, locale, isCollapsed } = this.props;

    return (
      <div
        data-hook={DH.PaymentDetails.Container}
        className={cn({
          [s.root]: true,
          [s.desktop]: deviceType === 'desktop',
          [s.mobile]: deviceType === 'mobile',
        })}
        dir={isRTLLocale(locale) ? 'rtl' : 'ltr'}
        ref={this.paymentDetailsRef}
      >
        <div className={s.fieldsGroup}>
          {!isCollapsed && this.renderMandatoryFields()}
        </div>
      </div>
    );
  }
}
